import { memo, useState, useCallback, useEffect, useRef } from "react";
import { useI18n } from "@/i18n";
import { listImportJobs } from "@/infrastructure/product/productService";
import type { ImportJobStatus } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

const STATUS_COLORS: Record<string, string> = {
  PENDING: "#f59e0b",
  PROCESSING: "#3b82f6",
  COMPLETED: "#16a34a",
  FAILED: "#ef4444",
};

export const NotificationBell = memo(function NotificationBell() {
  const { t } = useI18n();
  const [jobs, setJobs] = useState<ImportJobStatus[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchJobs = useCallback(async () => {
    try { setJobs(await listImportJobs()); } catch {}
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const hasActive = jobs.some(j => j.status === "PENDING" || j.status === "PROCESSING");
  const recentJobs = jobs.slice(0, 5);

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <button
        onClick={() => { setOpen(v => !v); if (!open) fetchJobs(); }}
        aria-label={t.platform.notifications}
        style={{ background: "none", border: "none", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", padding: "8px", borderRadius: "8px" }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {hasActive && (
          <span style={{ position: "absolute", top: "6px", right: "6px", width: "7px", height: "7px", borderRadius: "50%", background: "#3b82f6", border: "1.5px solid #f7f7f7" }} />
        )}
      </button>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: "300px", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "14px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", zIndex: 100, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #f2f2f2" }}>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{t.platform.notifications}</span>
          </div>
          {recentJobs.length === 0 && (
            <div style={{ padding: "24px 16px", textAlign: "center" }}>
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#bbb" }}>{t.platform.noNotifications}</span>
            </div>
          )}
          {recentJobs.map(job => (
            <JobItem key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
});

const JobItem = memo(function JobItem({ job }: { job: ImportJobStatus }) {
  const statusColor = STATUS_COLORS[job.status] ?? "#aaa";
  const progress = job.totalRows > 0 ? Math.round((job.processed / job.totalRows) * 100) : 0;

  const statusLabel = job.status === "COMPLETED"
    ? `${job.created.toLocaleString()} created`
    : job.status === "FAILED"
    ? "Failed"
    : `${progress}%`;

  const timeAgo = getTimeAgo(job.createdAt ?? "");

  return (
    <div style={{ padding: "12px 18px", borderBottom: "1px solid #f9f9f9", display: "flex", alignItems: "center", gap: "12px" }}>
      <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: statusColor, flexShrink: 0 }} />
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {job.fileName}
        </div>
        <div style={{ fontFamily: typography.fontFamily, fontSize: "10px", color: "#aaa", marginTop: "2px" }}>
          {statusLabel} · {timeAgo}
        </div>
      </div>
    </div>
  );
});

function getTimeAgo(dateStr: string): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
